"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { clearAuthUser, getAuthUser } from "../utils/auth";

const links = [
  { label: "Home", href: "#top" },
  { label: "About us", href: "#about" },
  { label: "Trading", href: "#ITO" },
  { label: "How to buy", href: "#how" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [shadow, setShadow] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hidden, setHidden] = useState(false);
  const navRef = useRef(null);
  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authUser, setAuthUser] = useState(() =>
    typeof window !== "undefined" ? getAuthUser() : null,
  );
  const location = useLocation();
  const navigate = useNavigate();
  const scrollOffset = 96;
  const hideOnMobilePrefixes = ["/dashboard", "/transactions", "/referrer-link", "/community", "/profile"];
  const hideNavOnMobile = hideOnMobilePrefixes.some((prefix) => location.pathname.startsWith(prefix));

  const toggleMenu = () => setOpen((prev) => !prev);
  const closeMenu = () => setOpen(false);
  const toggleUserMenu = () => setUserMenuOpen((previous) => !previous);
  const closeUserMenu = () => setUserMenuOpen(false);
  const updateAuthUser = () => {
    if (typeof window === "undefined") return;
    setAuthUser(getAuthUser());
  };
  const handleSmoothScroll = (event, target) => {
    if (!target?.startsWith("#") || typeof window === "undefined") {
      return;
    }
    event.preventDefault();
    closeMenu();
    closeUserMenu();
    if (location.pathname !== "/") {
      navigate("/", {
        state: { scrollTarget: target === "#top" ? "top" : target.slice(1) },
      });
      return;
    }
    if (target === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const element = document.querySelector(target);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - scrollOffset;
    window.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setShadow(currentY > 10);

      if (currentY > lastScrollY && currentY > 120) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      setLastScrollY(currentY);
    };

    const closeOnResize = () => {
      if (window.innerWidth >= 768) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", closeOnResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [lastScrollY]);

  useEffect(() => {
    if (!open || !navRef.current) return;

    const handleClickOutside = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) {
      document.body.style.removeProperty("overflow");
      return;
    }
    document.body.style.overflow = open ? "hidden" : "";
    return () => document.body.style.removeProperty("overflow");
  }, [open]);

  useEffect(() => {
    updateAuthUser();
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorageChange = () => {
      updateAuthUser();
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth-changed", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-changed", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  const navClasses = [
    "pointer-events-none fixed inset-x-0 top-0 z-50 px-3 py-2 transition-transform duration-300 will-change-transform",
    hidden ? "-translate-y-full" : "translate-y-0",
    hideNavOnMobile ? "hidden md:block" : "",
  ].join(" ");

  const surfaceClasses = [
    "pointer-events-auto mx-auto flex w-full max-w-[88rem] items-center justify-between rounded-2xl border px-4 py-3 backdrop-blur-xl transition-all duration-300 sm:w-[92%] md:w-[70%] lg:w-[80%]",
    shadow
      ? "border-white/20 bg-[linear-gradient(135deg,rgba(26,15,58,0.92),rgba(12,23,72,0.92))] shadow-[0_18px_45px_rgba(10,8,35,0.55)]"
      : "border-white/10 bg-[linear-gradient(135deg,rgba(26,15,58,0.75),rgba(12,23,72,0.75))]",
  ].join(" ");

  const displayName = useMemo(() => {
    if (!authUser) return "";
    return (
      authUser.name ||
      authUser.fullName ||
      authUser.username ||
      (authUser.email ? authUser.email.split("@")[0] : "")
    );
  }, [authUser]);

  const userInitials = useMemo(() => {
    if (!displayName) return "SC";
    return displayName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }, [displayName]);

  const avatarUrl =
    authUser?.avatarUrl || authUser?.avatar || authUser?.profileImage || authUser?.image || null;

  const handleSignOut = () => {
    clearAuthUser();
    closeMenu();
    closeUserMenu();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth-changed"));
    }
    navigate("/login");
  };

  return (
    <nav className={navClasses}>
      <div ref={navRef} className={surfaceClasses}>
        {/* Logo */}
      <Link to="/" className="flex items-center gap-2">
  <img
    src="/images/logos.png"
    alt="SynergyCoin Logo"
    className="h-10 w-auto flex-shrink-0 object-contain md:h-12 pt-3"
  />
  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] text-xl sm:text-2xl font-bold">
    SynergyCoin
  </span>
</Link>

        {/* Desktop links */}
        <ul className="hidden items-center gap-4 text-sm font-medium text-white/70 md:flex lg:gap-6 lg:text-base">
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(event) => handleSmoothScroll(event, link.href)}
                className="text-white/75 transition hover:text-white"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop auth controls */}
        {authUser ? (
          <div className="relative hidden items-center md:flex" ref={userMenuRef}>
            <button
              type="button"
              onClick={toggleUserMenu}
              className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/80 transition hover:border-white/30 hover:text-white"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName || "Profile"}
                  className="h-8 w-8 rounded-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="grid h-8 w-8 place-items-center rounded-full bg-[#6E3FCB]/60 text-xs font-semibold text-white">
                  {userInitials}
                </span>
              )}
              <span className="hidden lg:inline">{displayName || "Member"}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-4 w-4 transition ${
                  userMenuOpen ? "rotate-180 text-white" : "text-white/60"
                }`}
              >
                <path
                  fillRule="evenodd"
                  d="M10 12a.75.75 0 0 1-.53-.22l-3.25-3.25a.75.75 0 0 1 1.06-1.06L10 10.69l2.72-2.72a.75.75 0 0 1 1.06 1.06l-3.25 3.25A.75.75 0 0 1 10 12Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 overflow-hidden rounded-2xl border border-white/10 bg-[#0B0718]/95 text-sm text-white/80 shadow-[0_18px_36px_rgba(8,5,20,0.55)] backdrop-blur-md">
                <Link
                  to="/dashboard"
                  onClick={closeUserMenu}
                  className="block px-4 py-3 transition hover:bg-white/10 hover:text-white"
                >
                  Dashboard
                </Link>
                <Link
                  to="/profile"
                  onClick={closeUserMenu}
                  className="block px-4 py-3 transition hover:bg-white/10 hover:text-white"
                >
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-red-200 transition hover:bg-red-500/10 hover:text-red-100"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/login"
              className="text-sm text-white/75 transition hover:text-white lg:text-base"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="rounded-md bg-gradient-to-r from-[#6418C3] to-[#9246FF] px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(112,63,207,0.45)] transition hover:brightness-105 lg:px-5 lg:py-2.5 lg:text-base"
            >
              Sign up
            </Link>
          </div>
        )}

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={toggleMenu}
          aria-expanded={open}
          aria-controls="mobile-nav"
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition hover:bg-white/20 md:hidden sm:h-10 sm:w-10"
        >
          <span className="sr-only">Toggle menu</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-5 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.6}
          >
            {open ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        id="mobile-nav"
        className={`pointer-events-auto mx-auto mt-2 w-full max-w-[88rem] overflow-hidden rounded-3xl border border-white/12 bg-[#070319] text-white/85 shadow-[0_18px_42px_rgba(7,6,22,0.65)] transition-all duration-200 md:hidden sm:w-[92%] lg:w-[80%] ${
          open ? "max-h-[32rem] px-4 py-6 opacity-100" : "max-h-0 px-4 py-0 opacity-0"
        }`}
      >
        <ul className="flex flex-col gap-3">
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(event) => handleSmoothScroll(event, link.href)}
                className="block rounded-lg px-3 py-3 text-sm transition hover:bg-white/10 hover:text-white sm:text-base"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {authUser ? (
          <div className="mt-5 space-y-3 rounded-2xl border border-white/12 bg-[#0E0A1F] p-4 text-sm text-white/90 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName || "Profile"}
                  className="h-10 w-10 rounded-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[#6E3FCB]/60 text-sm font-semibold text-white">
                  {userInitials}
                </span>
              )}
              <div>
                <div className="font-semibold text-white">{displayName || "Member"}</div>
                <div className="text-xs text-white/60">Signed in</div>
              </div>
            </div>
            <Link
              to="/dashboard"
              onClick={closeMenu}
              className="block rounded-lg border border-white/15 bg-white/5 px-3 py-3 text-center font-medium text-white transition hover:border-white/30 hover:bg-white/10"
            >
              Dashboard
            </Link>
            <Link
              to="/profile"
              onClick={closeMenu}
              className="block rounded-lg border border-white/15 bg-white/5 px-3 py-3 text-center font-medium text-white transition hover:border-white/30 hover:bg-white/10"
            >
              Profile
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-3 w-full rounded-xl border border-red-400/50 bg-gradient-to-r from-[#FF5F6D] to-[#FF3D57] px-4 py-3 text-center text-base font-semibold text-white shadow-[0_12px_28px_rgba(255,61,87,0.35)] transition hover:brightness-110"
            >
              Sign out
            </button>
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-3">
            <Link
              to="/login"
              onClick={closeMenu}
              className="rounded-lg border border-white/20 px-3 py-3 text-center text-sm transition hover:border-white/35 hover:text-white sm:text-base"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              onClick={closeMenu}
              className="rounded-lg bg-gradient-to-r from-[#6418C3] to-[#9246FF] px-3 py-3 text-center text-sm font-semibold text-white shadow-[0_12px_28px_rgba(112,63,207,0.45)] transition hover:brightness-105 sm:text-base"
            >
              Sign up
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
